import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import React, { useState } from 'react'
import {
  Action,
  Content,
  Description,
  DismissButton,
  IconWrapper,
  StyledCallout,
  Title,
} from 'sutra/components/Callout/callout-styles'
import { Close } from 'sutra/components/Icons'
import { accents } from 'sutra/tokens/colors'

export type CalloutVariant = 'info' | 'success' | 'warning' | 'danger'

export interface CalloutProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  variant?: CalloutVariant
  /** Bold heading line. Content, not the native tooltip attribute. */
  title?: React.ReactNode
  /** Rendered under the title; also gives the callout roomier padding. */
  description?: React.ReactNode
  /** Trailing slot, rendered before the dismiss button. */
  action?: React.ReactNode
  /** Shows the dismiss button; called once when it is clicked. */
  onClose?: () => void
  /** Shows the dismiss button, which hides the callout when clicked. */
  dismissable?: boolean
}

const ICON_SX = { fontSize: 18, flexShrink: 0 } as const

const VARIANT_ICON: Record<CalloutVariant, React.ReactElement> = {
  info: (
    <InfoRoundedIcon sx={{ ...ICON_SX, color: accents.reflectionBlue[500] }} />
  ),
  success: (
    <CheckCircleRoundedIcon
      sx={{ ...ICON_SX, color: accents.emeraldAura[500] }}
    />
  ),
  warning: (
    <WarningRoundedIcon sx={{ ...ICON_SX, color: accents.goldenLight[600] }} />
  ),
  danger: (
    <ErrorRoundedIcon sx={{ ...ICON_SX, color: accents.crimsonBloom[500] }} />
  ),
}

export const Callout = ({
  variant = 'info',
  title,
  description,
  action,
  onClose,
  dismissable = false,
  children,
  ...divProps
}: CalloutProps): React.ReactElement | null => {
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null

  const handleDismiss = (): void => {
    onClose?.()
    setDismissed(true)
  }

  return (
    <StyledCallout
      {...divProps}
      $variant={variant}
      $roomy={description != null}
    >
      <IconWrapper>{VARIANT_ICON[variant]}</IconWrapper>
      <Content>
        {title != null && <Title>{title}</Title>}
        {description != null && <Description>{description}</Description>}
        {children}
      </Content>
      {action != null && <Action>{action}</Action>}
      {(dismissable || onClose) && (
        <DismissButton
          type="button"
          aria-label="Dismiss"
          onClick={handleDismiss}
        >
          <Close />
        </DismissButton>
      )}
    </StyledCallout>
  )
}

Callout.displayName = 'Callout'
